import { IndicatorSeries, type Bounding, type IndicatorTemplate } from 'klinecharts';
import { useChartStore } from '@/store/chartStore';
import type { CorporateEvent, CorporateEventType } from '@/types';

type KBar = { timestamp: number };

type DrawParams = {
  ctx: CanvasRenderingContext2D;
  kLineDataList: KBar[];
  visibleRange: { from: number; to: number };
  xAxis: { convertToPixel: (i: number) => number };
  bounding: Bounding;
};

// ── Marker visual config per event type ────────────────────────────────────
const EVENT_CONFIG: Record<CorporateEventType, { label: string; color: string }> = {
  div:           { label: 'D',  color: '#26A69A' },
  div_script:    { label: 'SD', color: '#80CBC4' },
  div_ann:       { label: 'DA', color: '#4DB6AC' },
  rights:        { label: 'R',  color: '#42A5F5' },
  rights_ann:    { label: 'RA', color: '#90CAF9' },
  split:             { label: 'S',  color: '#AB47BC' },
  split_ann:         { label: 'SA', color: '#CE93D8' },
  consolidation:     { label: 'C',  color: '#EF5350' },
  consolidation_ann: { label: 'CA', color: '#EF9A9A' },
  report:            { label: 'F',  color: '#FFA726' },
  agm:               { label: 'AG', color: '#78909C' },
  egm:               { label: 'EG', color: '#90A4AE' },
  halt:              { label: 'H',  color: '#B71C1C' },
};

// ── Date helpers ────────────────────────────────────────────────────────────
function tsToDateStr(tsMs: number): string {
  const d = new Date(tsMs);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** For 1W bars: the bar's timestamp (Mon) up to +6 days */
function dateInWeek(date: string, barDateStr: string): boolean {
  const d = Date.parse(date);
  const b = Date.parse(barDateStr);
  return d >= b && d <= b + 6 * 86_400_000;
}

/** For 1M bars: same year+month */
function dateInMonth(date: string, barDateStr: string): boolean {
  return date.slice(0, 7) === barDateStr.slice(0, 7);
}

// ── Marker drawing ──────────────────────────────────────────────────────────
const MARKER_W = 14;
const MARKER_H = 12;
const MARKER_GAP = 2; // vertical gap between stacked markers
const FONT = `bold 9px sans-serif`;

function drawTriangle(
  ctx: CanvasRenderingContext2D,
  cx: number,
  baseY: number,
  color: string,
  label: string,
) {
  ctx.save();
  ctx.fillStyle = color;
  ctx.strokeStyle = color;

  // Upward-pointing triangle
  ctx.beginPath();
  ctx.moveTo(cx, baseY - MARKER_H);         // apex
  ctx.lineTo(cx - MARKER_W / 2, baseY);     // bottom-left
  ctx.lineTo(cx + MARKER_W / 2, baseY);     // bottom-right
  ctx.closePath();
  ctx.fill();

  // Label inside
  ctx.fillStyle = '#ffffff';
  ctx.font = FONT;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label.length > 1 ? label[0] : label, cx, baseY - MARKER_H / 2 - 1);

  ctx.restore();
}

// ── Build date→events map on demand (memoised per events array identity) ───
let _lastEventsRef: CorporateEvent[] | null = null;
let _lastMap: Map<string, CorporateEvent[]> = new Map();

function buildDateMap(events: CorporateEvent[]): Map<string, CorporateEvent[]> {
  if (events === _lastEventsRef) return _lastMap;
  _lastEventsRef = events;
  const map = new Map<string, CorporateEvent[]>();
  for (const ev of events) {
    const list = map.get(ev.date) ?? [];
    list.push(ev);
    map.set(ev.date, list);
  }
  _lastMap = map;
  return map;
}

// Priority order for rendering (most important on top/nearest to price pane)
const TYPE_PRIORITY: CorporateEventType[] = [
  'halt', 'split', 'consolidation', 'rights', 'rights_ann',
  'div', 'div_script', 'div_ann', 'split_ann', 'consolidation_ann',
  'agm', 'egm', 'report',
];

function sortByPriority(events: CorporateEvent[]): CorporateEvent[] {
  return [...events].sort(
    (a, b) => TYPE_PRIORITY.indexOf(a.type) - TYPE_PRIORITY.indexOf(b.type),
  );
}

// ── Shared layout store for hover detection (written each draw) ─────────────
export type EventMarkerLayout = {
  barIndex: number;
  events: CorporateEvent[];
  x: number;
  /** bottom pixel of the topmost marker in the stack */
  topY: number;
  /** bottom pixel of the bottommost marker */
  bottomY: number;
};

export const eventMarkerLayouts: EventMarkerLayout[] = [];

// ── The custom KLineCharts indicator ──────────────────────────────────────────
const eventMarkers: IndicatorTemplate<Record<string, never>> = {
  name: 'EVENT_MARKERS',
  shortName: 'Events',
  series: IndicatorSeries.Normal,
  shouldOhlc: false,
  figures: [],

  calc: (dataList) => dataList.map(() => ({})),

  draw: (params: unknown): boolean => {
    try {
      const { ctx, kLineDataList: bars, visibleRange, xAxis, bounding } = params as DrawParams;

      if (!bars.length) return true;

      const state = useChartStore.getState();
      if (!state.showEvents) return true;

      const { corporateEvents, timeframe } = state;
      if (!corporateEvents.length) return true;

      const dateMap = buildDateMap(corporateEvents);
      const isWeekly = timeframe === '1W';
      const isMonthly = timeframe === '1M';

      // Bottom Y of the candle pane — markers drawn from here upward
      const paneBottom = bounding.top + bounding.height - 2;

      // Clear layout store for this draw pass
      eventMarkerLayouts.length = 0;

      for (let i = visibleRange.from; i <= visibleRange.to; i++) {
        const bar = bars[i];
        if (!bar) continue;
        const barDateStr = tsToDateStr(bar.timestamp);

        // Collect events that fall on or within this bar's period
        let barEvents: CorporateEvent[] = [];

        if (isWeekly) {
          for (const [date, evs] of dateMap) {
            if (dateInWeek(date, barDateStr)) barEvents.push(...evs);
          }
        } else if (isMonthly) {
          for (const [date, evs] of dateMap) {
            if (dateInMonth(date, barDateStr)) barEvents.push(...evs);
          }
        } else {
          barEvents = dateMap.get(barDateStr) ?? [];
        }

        if (!barEvents.length) continue;

        const sorted = sortByPriority(barEvents);
        const x = xAxis.convertToPixel(i);
        let baseY = paneBottom;

        const layout: EventMarkerLayout = {
          barIndex: i,
          events: sorted,
          x,
          topY: paneBottom,
          bottomY: paneBottom,
        };

        sorted.forEach((ev, idx) => {
          const cfg = EVENT_CONFIG[ev.type];
          drawTriangle(ctx, x, baseY, cfg.color, cfg.label);
          if (idx === 0) layout.bottomY = baseY;
          layout.topY = baseY - MARKER_H;
          baseY -= MARKER_H + MARKER_GAP;
        });

        eventMarkerLayouts.push(layout);
      }
    } catch {
      // Swallow to avoid breaking the chart render loop
    }
    return true;
  },
};

export default eventMarkers;
